// cleancode-ui/src/components/SignUp.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Login.css"; // Reuse the same beautiful styling
import logo from "../assets/logo.png";
import { useEnvironment } from "../contexts/EnvironmentContext";

const plans = [
  { 
    id: 'solo', 
    name: 'Solo', 
    price: '$12/month', 
    trialPrice: '$0 for 7 days',
    description: 'Perfect for individual developers',
    features: ['10 code reviews per month', 'AI-powered analysis', 'GitHub PR reviews', 'Review history']
  },
  { 
    id: 'pro', 
    name: 'Pro', 
    price: '$25/month', 
    trialPrice: '$0 for 7 days',
    description: 'For professional developers who need more reviews',
    features: ['50 code reviews per month', 'AI-powered analysis', 'GitHub PR reviews', 'Review history']
  }
];

const SignUp = () => {
  const navigate = useNavigate();
  const { apiBaseUrl } = useEnvironment();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [plan, setPlan] = useState("starter");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // null, 'checking', 'available', 'taken'
  const [emailMessage, setEmailMessage] = useState("");

  // Debounced email checking
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (email && email.includes('@') && email.includes('.')) {
        checkEmailAvailability(email);
      } else {
        setEmailStatus(null);
        setEmailMessage("");
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [email]);

  const checkEmailAvailability = async (emailToCheck) => {
    console.log('Checking email:', emailToCheck);
    setEmailStatus('checking');
    setEmailMessage("Checking email availability...");
    
    try {
      const res = await fetch(`${apiBaseUrl}/auth/check-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailToCheck, password: "dummy" }), // password not needed for check
      });

      if (res.ok) {
        const data = await res.json();
        console.log('Email check response:', data);
        setEmailStatus(data.available ? 'available' : 'taken');
        setEmailMessage(data.message);
      } else {
        console.log('Email check failed:', res.status);
        setEmailStatus('taken');
        setEmailMessage("Email is already taken");
      }
    } catch (err) {
      console.log('Email check error:', err);
      setEmailStatus('taken');
      setEmailMessage("Unable to check email availability");
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError("");
    
    console.log('Form submission - emailStatus:', emailStatus, 'email:', email);

    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    if (emailStatus === 'taken') {
      setError("Email is already taken. Please use a different email address.");
      return;
    }

    if (emailStatus === 'checking') {
      setError("Please wait while we check your email availability.");
      return;
    }

    if (emailStatus === null || emailStatus === 'available') {
      // Only proceed if email is confirmed available
      if (emailStatus === null) {
        setError("Please wait for email validation to complete.");
        return;
      }
    }

    setLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Registration failed");
      }

      // Auto-login after successful registration
      const loginRes = await fetch(`${apiBaseUrl}/auth/jwt/login-form`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ username: email, password }),
      });

      if (loginRes.ok) {
        const loginData = await loginRes.json();
        localStorage.setItem("token", loginData.access_token);
        localStorage.setItem("userEmail", email); // Store user's email
      }

      const checkout = await fetch(`${apiBaseUrl}/billing/create-checkout-session/${plan}`, {
        method: "POST",
      });

      if (!checkout.ok) {
        const data = await checkout.json();
        throw new Error(data.detail || "Failed to start subscription");
      }

      const { url } = await checkout.json();
      window.location.href = url;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="logo-container">
            <img src={logo} alt="Logo" width={150} />
          </div>
          <p className="login-subtitle">Join CleanCode.ai today</p>
        </div>

        <form onSubmit={handleSignUp} className="login-form">
          <div className="input-group">
            <input
              type="email"
              placeholder="Email address"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`login-input ${emailStatus === 'taken' ? 'input-error' : emailStatus === 'available' ? 'input-success' : ''}`}
              disabled={loading}
            />
            {emailMessage && (
              <div className={`email-status ${emailStatus === 'taken' ? 'status-error' : emailStatus === 'available' ? 'status-success' : 'status-checking'}`}>
                {emailStatus === 'checking' && '⏳'}
                {emailStatus === 'available' && '✅'}
                {emailStatus === 'taken' && '❌'}
                {emailMessage}
              </div>
            )}
          </div>

          <div className="input-group">
            <input
              type="password"
              placeholder="Password (min 8 characters)"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="login-input"
              disabled={loading}
            />
          </div>

          <div className="input-group">
            <input
              type="password"
              placeholder="Confirm password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="login-input"
              disabled={loading}
            />
          </div>

          <div className="input-group">
            <label className="plan-label">Choose Your Plan</label>
            <div className="plan-selection">
              {plans.map((p) => (
                <div 
                  key={p.id} 
                  className={`plan-option ${plan === p.id ? 'selected' : ''}`}
                  onClick={() => setPlan(p.id)}
                >
                  <div className="plan-header">
                    <h3 className="plan-name">{p.name}</h3>
                    <div className="plan-pricing">
                      <span className="trial-price">{p.trialPrice}</span>
                      <span className="regular-price">then {p.price}</span>
                    </div>
                  </div>
                  <p className="plan-description">{p.description}</p>
                  <ul className="plan-features">
                    {p.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div className="error-message">
              <svg
                className="error-icon"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {error}
            </div>
          )}

          <button
            type="submit"
            className={`login-button ${loading ? "loading" : ""}`}
            disabled={loading}
          >
            {loading ? (
              <>
                <svg className="spinner" viewBox="0 0 24 24">
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray="31.416"
                    strokeDashoffset="31.416"
                  >
                    <animate
                      attributeName="stroke-dasharray"
                      dur="2s"
                      values="0 31.416;15.708 15.708;0 31.416"
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="stroke-dashoffset"
                      dur="2s"
                      values="0;-15.708;-31.416"
                      repeatCount="indefinite"
                    />
                  </circle>
                </svg>
                Creating account...
              </>
            ) : (
              "Start 7-Day Free Trial"
            )}
          </button>
        </form>

        <div className="login-footer">
          <p className="footer-text">
            Secure registration powered by FastAPI
          </p>
          <div className="auth-switch">
            <p className="switch-text">
              Already have an account?{" "}
              <button 
                type="button" 
                className="switch-button"
                onClick={() => navigate('/login')}
              >
                Sign in to CleanCode.ai
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
